import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCategories, createProduct } from "../../services/adminService";

const emptyVariant = () => ({
	name: "",
	slug: "",
	description: "",
	price: "",
	images: [],
	available: true,
	stock: 100,
	sort_order: 0,
});

const slugify = (value = "") => {
	return value
		.toLowerCase()
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");
};

const AdminAddProductPage = () => {
	const navigate = useNavigate();

	const [categories, setCategories] = useState([]);
	const [loadingCategories, setLoadingCategories] = useState(true);
	const [saving, setSaving] = useState(false);

	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");
	const [createdProductId, setCreatedProductId] = useState(null);

	const [form, setForm] = useState({
		name: "",
		slug: "",
		short_description: "",
		description: "",
		price: "",
		images: [],
		category_id: "",
		tags: [],
		available: true,
		featured: false,
		stock: 100,
		has_variants: false,
	});

	const [tagInput, setTagInput] = useState("");
	const [variants, setVariants] = useState([]);

	/*
	 * CHARGEMENT DES CATÉGORIES
	 */
	useEffect(() => {
		const loadCategories = async () => {
			try {
				const data = await getCategories();
				setCategories(data || []);
			} catch (err) {
				console.error("Erreur chargement catégories:", err);
				setError("Impossible de charger les catégories.");
			} finally {
				setLoadingCategories(false);
			}
		};

		loadCategories();
	}, []);

	/*
	 * SLUG AUTOMATIQUE
	 */
	const generatedSlug = useMemo(() => {
		if (form.slug.trim()) {
			return form.slug.trim();
		}

		return slugify(form.name);
	}, [form.name, form.slug]);

	/*
	 * CHANGEMENT DES CHAMPS PRODUIT
	 */
	const handleChange = (event) => {
		const { name, value, type, checked } = event.target;

		setForm((current) => ({
			...current,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	/*
	 * TAGS
	 */
	const handleAddTag = (event) => {
		if (event.key !== "Enter") return;

		event.preventDefault();

		const tag = tagInput.trim();

		if (!tag) return;

		if (!form.tags.includes(tag)) {
			setForm((current) => ({
				...current,
				tags: [...current.tags, tag],
			}));
		}

		setTagInput("");
	};

	const removeTag = (tagToRemove) => {
		setForm((current) => ({
			...current,
			tags: current.tags.filter((tag) => tag !== tagToRemove),
		}));
	};

	/*
	 * VARIANTES
	 */
	const addVariant = () => {
		setVariants((current) => [
			...current,
			{
				...emptyVariant(),
				sort_order: current.length,
			},
		]);

		setForm((current) => ({
			...current,
			has_variants: true,
		}));
	};

	const updateVariant = (index, field, value) => {
		setVariants((current) =>
			current.map((variant, variantIndex) =>
				variantIndex === index
					? {
							...variant,
							[field]: value,
						}
					: variant,
			),
		);
	};

	const removeVariant = (index) => {
		setVariants((current) => {
			const next = current
				.filter((_, variantIndex) => variantIndex !== index)
				.map((variant, variantIndex) => ({
					...variant,
					sort_order: variantIndex,
				}));

			if (next.length === 0) {
				setForm((currentForm) => ({
					...currentForm,
					has_variants: false,
				}));
			}

			return next;
		});
	};

	/*
	 * SOUMISSION
	 */
	const handleSubmit = async (event) => {
		event.preventDefault();

		setError("");
		setSuccess("");
		setCreatedProductId(null);

		/*
		 * VALIDATION PRODUIT
		 */
		const productName = form.name.trim();

		if (!productName) {
			setError("Le nom du produit est obligatoire.");
			return;
		}

		if (!form.price || Number(form.price) < 0) {
			setError("Le prix du produit est obligatoire.");
			return;
		}

		const finalSlug = generatedSlug;

		if (!finalSlug) {
			setError("Impossible de générer le slug du produit.");
			return;
		}

		if (form.stock === "" || Number(form.stock) < 0) {
			setError("Le stock du produit doit être supérieur ou égal à 0.");
			return;
		}

		/*
		 * VALIDATION VARIANTES
		 */
		if (form.has_variants && variants.length === 0) {
			setError(
				"Ajoute au moins une variante ou désactive l'option « Produit avec variantes ».",
			);
			return;
		}

		for (const variant of variants) {
			if (!variant.name.trim()) {
				setError("Chaque variante doit avoir un nom.");
				return;
			}

			if (variant.price === "" || Number(variant.price) < 0) {
				setError(
					`La variante « ${variant.name || "sans nom"} » doit avoir un prix.`,
				);
				return;
			}

			if (variant.stock === "" || Number(variant.stock) < 0) {
				setError(`Le stock de la variante « ${variant.name} » est invalide.`);
				return;
			}
		}
		try {
			setSaving(true);

			const payload = {
				...form,
				slug: finalSlug,
				price: Number(form.price),
				stock: Number(form.stock),
				category_id: form.category_id || null,

				images: form.images,

				variants: form.has_variants
					? variants.map((variant, index) => ({
							...variant,
							name: variant.name.trim(),
							slug: variant.slug.trim() || slugify(variant.name),
							description: variant.description?.trim() || null,
							price: Number(variant.price),
							stock: Number(variant.stock),
							sort_order: index,
							images: variant.images || [],
						}))
					: [],
			};

			const productId = await createProduct(payload);

			setCreatedProductId(productId);
			setSuccess(`Produit créé avec succès. Identifiant : ${productId}`);

			setTimeout(() => {
				navigate("/admin/products");
			}, 800);
		} catch (err) {
			console.error("Erreur création produit:", err);

			setError(err?.message || "Impossible de créer le produit.");
		} finally {
			setSaving(false);
		}
	};

	return (
		<div className="max-w-7xl mx-auto px-4 pt-32 pb-10">
			{/* HEADER */}
			<div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
				<div>
					<p className="text-sm text-[#8A8175] mb-2">Administration</p>

					<h1 className="text-3xl font-bold text-[#2A2A2A]">
						Ajouter un produit
					</h1>

					<p className="text-[#6B6B6B] mt-2">
						Crée un nouveau produit pour la boutique.
					</p>
				</div>

				<Link
					to="/admin/products"
					className="inline-flex items-center justify-center rounded-xl border border-[#E6E1D6] bg-white px-5 py-3 text-sm font-medium text-[#2A2A2A] hover:bg-[#FFFCF8] transition"
				>
					← Retour aux produits
				</Link>
			</div>

			{/* ERREUR */}
			{error && (
				<div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
					{error}
				</div>
			)}

			{/* SUCCÈS */}
			{success && (
				<div className="mb-6 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
					{success}
				</div>
			)}

			<form onSubmit={handleSubmit}>
				<div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
					{/* =========================================
					    COLONNE PRINCIPALE
					========================================= */}
					<div className="xl:col-span-2 space-y-6">
						{/* INFORMATIONS GÉNÉRALES */}
						<section className="bg-white border border-[#E6E1D6] rounded-2xl p-6 shadow-sm">
							<h2 className="text-xl font-semibold text-[#2A2A2A] mb-6">
								Informations générales
							</h2>

							<div className="space-y-5">
								{/* NOM */}
								<div>
									<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
										Nom du produit *
									</label>

									<input
										type="text"
										name="name"
										value={form.name}
										onChange={handleChange}
										placeholder="Ex. Cappuccino"
										className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3 outline-none focus:border-[#8A8175]"
										required
									/>
								</div>

								{/* SLUG */}
								<div>
									<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
										Slug
									</label>

									<input
										type="text"
										name="slug"
										value={form.slug}
										onChange={handleChange}
										placeholder={generatedSlug || "cappuccino"}
										className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3 outline-none focus:border-[#8A8175]"
									/>

									<p className="text-xs text-[#8A8175] mt-2">
										Le slug est généré automatiquement s'il est laissé vide.
									</p>
								</div>

								{/* DESCRIPTION COURTE */}
								<div>
									<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
										Description courte
									</label>

									<textarea
										name="short_description"
										value={form.short_description}
										onChange={handleChange}
										rows={3}
										placeholder="Une courte présentation du produit..."
										className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3 outline-none focus:border-[#8A8175] resize-none"
									/>
								</div>

								{/* DESCRIPTION */}
								<div>
									<label className="block text-sm font-medium text-[#2A2A2A] mb-2">
										Description complète
									</label>

									<textarea
										name="description"
										value={form.description}
										onChange={handleChange}
										rows={8}
										placeholder="Décris le produit en détail..."
										className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3 outline-none focus:border-[#8A8175] resize-y"
									/>
								</div>
							</div>
						</section>

						{/* IMAGE */}
						<section className="bg-white border border-[#E6E1D6] rounded-2xl p-6 shadow-sm">
							<h2 className="text-xl font-semibold text-[#2A2A2A] mb-6">
								Image du produit
							</h2>

							<div>
								<label className="block text-sm font-medium mb-2">
									Images du produit
								</label>

								<input
									type="file"
									accept="image/*"
									multiple
									onChange={(e) => {
										const files = Array.from(e.target.files || []);

										setForm((prev) => ({
											...prev,
											images: files,
										}));
									}}
								/>

								{form.images.length > 0 && (
									<div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
										{form.images.map((file, index) => (
											<div key={`${file.name}-${index}`} className="relative">
												<img
													src={URL.createObjectURL(file)}
													alt={file.name}
													className="w-full aspect-square object-cover rounded-lg"
												/>

												<button
													type="button"
													onClick={() => {
														setForm((prev) => ({
															...prev,
															images: prev.images.filter((_, i) => i !== index),
														}));
													}}
													className="absolute top-2 right-2 bg-black/70 text-white rounded-full px-2 py-1"
												>
													×
												</button>
											</div>
										))}
									</div>
								)}
							</div>
						</section>

						{/* VARIANTES */}
						<section className="bg-white border border-[#E6E1D6] rounded-2xl p-6 shadow-sm">
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
								<div>
									<div className="flex items-center gap-3">
										<h2 className="text-xl font-semibold text-[#2A2A2A]">
											Variantes
										</h2>

										{variants.length > 0 && (
											<span className="rounded-full bg-green-50 border border-green-200 px-2.5 py-1 text-xs font-medium text-green-700">
												{variants.length}{" "}
												{variants.length > 1 ? "variantes" : "variante"}
											</span>
										)}
									</div>

									<p className="text-sm text-[#6B6B6B] mt-1">
										Ajoute des formats, tailles ou versions avec leur propre
										prix, stock et images.
									</p>
								</div>

								<button
									type="button"
									onClick={addVariant}
									className="rounded-xl bg-[#2A2A2A] text-white px-4 py-2.5 text-sm font-medium hover:bg-[#3B3B3B] transition"
								>
									+ Ajouter une variante
								</button>
							</div>

							{variants.length === 0 ? (
								<div className="rounded-xl border border-dashed border-[#DDD7CB] p-6 text-center text-sm text-[#8A8175]">
									Aucune variante pour le moment.
								</div>
							) : (
								<div className="space-y-5">
									{variants.map((variant, index) => (
										<div
											key={index}
											className="rounded-2xl border border-[#E6E1D6] p-5 bg-[#FFFCF8]"
										>
											<div className="flex justify-between items-center mb-5">
												<h3 className="font-semibold text-[#2A2A2A]">
													Variante {index + 1}
												</h3>

												<button
													type="button"
													onClick={() => removeVariant(index)}
													className="text-sm text-red-600 hover:text-red-800"
												>
													Supprimer
												</button>
											</div>

											<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
												{/* NOM */}
												<div>
													<label className="block text-sm font-medium mb-2">
														Nom *
													</label>

													<input
														type="text"
														value={variant.name}
														onChange={(e) =>
															updateVariant(index, "name", e.target.value)
														}
														placeholder="Grand"
														className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3"
													/>
												</div>

												{/* PRIX */}
												<div>
													<label className="block text-sm font-medium mb-2">
														Prix CHF *
													</label>

													<input
														type="number"
														min="0"
														step="0.01"
														value={variant.price}
														onChange={(e) =>
															updateVariant(index, "price", e.target.value)
														}
														className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3"
													/>
												</div>

												{/* STOCK */}
												<div>
													<label className="block text-sm font-medium mb-2">
														Stock
													</label>

													<input
														type="number"
														min="0"
														value={variant.stock}
														onChange={(e) =>
															updateVariant(index, "stock", e.target.value)
														}
														className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3"
													/>
												</div>

												{/* IMAGE */}
												{/* IMAGES */}
												<div className="md:col-span-2">
													<label className="block text-sm font-medium mb-2">
														Images de la variante
													</label>

													<input
														type="file"
														accept="image/*"
														multiple
														onChange={(e) => {
															const files = Array.from(e.target.files || []);

															updateVariant(index, "images", files);
														}}
														className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3 bg-white"
													/>

													{variant.images?.length > 0 && (
														<div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
															{variant.images.map((file, imageIndex) => (
																<div
																	key={`${file.name}-${imageIndex}`}
																	className="relative"
																>
																	<img
																		src={URL.createObjectURL(file)}
																		alt={file.name}
																		className="w-full aspect-square object-cover rounded-lg"
																	/>

																	<button
																		type="button"
																		onClick={() => {
																			updateVariant(
																				index,
																				"images",
																				variant.images.filter(
																					(_, i) => i !== imageIndex,
																				),
																			);
																		}}
																		className="absolute top-2 right-2 bg-black/70 text-white rounded-full px-2 py-1"
																	>
																		×
																	</button>
																</div>
															))}
														</div>
													)}
												</div>

												{/* DESCRIPTION */}
												<div className="md:col-span-2">
													<label className="block text-sm font-medium mb-2">
														Description
													</label>

													<textarea
														rows={3}
														value={variant.description}
														onChange={(e) =>
															updateVariant(
																index,
																"description",
																e.target.value,
															)
														}
														className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3 resize-none"
													/>
												</div>
											</div>

											{/* DISPONIBILITÉ VARIANTE */}
											<label className="flex items-center gap-3 mt-4 text-sm">
												<input
													type="checkbox"
													checked={variant.available}
													onChange={(e) =>
														updateVariant(index, "available", e.target.checked)
													}
												/>
												Variante disponible
											</label>
										</div>
									))}
								</div>
							)}
						</section>
					</div>

					{/* =========================================
					    SIDEBAR
					========================================= */}
					<div className="space-y-6">
						{/* VENTE */}
						<section className="bg-white border border-[#E6E1D6] rounded-2xl p-6 shadow-sm">
							<h2 className="text-xl font-semibold text-[#2A2A2A] mb-6">
								Vente
							</h2>

							<div className="space-y-5">
								{/* PRIX */}
								<div>
									<label className="block text-sm font-medium mb-2">
										Prix CHF *
									</label>

									<input
										type="number"
										name="price"
										min="0"
										step="0.01"
										value={form.price}
										onChange={handleChange}
										placeholder="5.50"
										className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3"
										required
									/>
								</div>

								{/* STOCK */}
								<div>
									<label className="block text-sm font-medium mb-2">
										Stock
									</label>

									<input
										type="number"
										name="stock"
										min="0"
										value={form.stock}
										onChange={handleChange}
										className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3"
									/>
								</div>

								{/* CATÉGORIE */}
								<div>
									<label className="block text-sm font-medium mb-2">
										Catégorie
									</label>

									<select
										name="category_id"
										value={form.category_id}
										onChange={handleChange}
										disabled={loadingCategories}
										className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3 bg-white"
									>
										<option value="">
											{loadingCategories ? "Chargement..." : "Sans catégorie"}
										</option>

										{categories.map((category) => (
											<option key={category.id} value={category.id}>
												{category.name}
											</option>
										))}
									</select>
								</div>
							</div>
						</section>

						{/* VISIBILITÉ */}
						<section className="bg-white border border-[#E6E1D6] rounded-2xl p-6 shadow-sm">
							<h2 className="text-xl font-semibold text-[#2A2A2A] mb-6">
								Visibilité
							</h2>

							<div className="space-y-4">
								{/* AVAILABLE */}
								<label className="flex items-start gap-3">
									<input
										type="checkbox"
										name="available"
										checked={form.available}
										onChange={handleChange}
										className="mt-1"
									/>

									<div>
										<p className="font-medium text-[#2A2A2A]">
											Produit disponible
										</p>

										<p className="text-xs text-[#6B6B6B] mt-1">
											Le produit peut être acheté.
										</p>
									</div>
								</label>

								{/* FEATURED */}
								<label className="flex items-start gap-3">
									<input
										type="checkbox"
										name="featured"
										checked={form.featured}
										onChange={handleChange}
										className="mt-1"
									/>

									<div>
										<p className="font-medium text-[#2A2A2A]">
											Produit mis en avant
										</p>

										<p className="text-xs text-[#6B6B6B] mt-1">
											Apparaît dans les produits recommandés / featured.
										</p>
									</div>
								</label>
							</div>
						</section>

						{/* TAGS */}
						<section className="bg-white border border-[#E6E1D6] rounded-2xl p-6 shadow-sm">
							<h2 className="text-xl font-semibold text-[#2A2A2A] mb-4">
								Tags
							</h2>

							<input
								type="text"
								value={tagInput}
								onChange={(e) => setTagInput(e.target.value)}
								onKeyDown={handleAddTag}
								placeholder="Ex. café, chaud..."
								className="w-full rounded-xl border border-[#DDD7CB] px-4 py-3"
							/>

							<p className="text-xs text-[#8A8175] mt-2">
								Appuie sur Entrée pour ajouter un tag.
							</p>

							{form.tags.length > 0 && (
								<div className="flex flex-wrap gap-2 mt-4">
									{form.tags.map((tag) => (
										<button
											key={tag}
											type="button"
											onClick={() => removeTag(tag)}
											className="rounded-full bg-[#F2EEE6] px-3 py-1.5 text-sm text-[#5E574D]"
										>
											{tag} ×
										</button>
									))}
								</div>
							)}
						</section>

						{/* CRÉATION */}
						<button
							type="submit"
							disabled={saving}
							className="w-full rounded-xl bg-[#2A2A2A] text-white px-5 py-4 font-semibold hover:bg-[#3B3B3B] transition disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{saving ? "Création en cours..." : "Créer le produit"}
						</button>

						{createdProductId && (
							<p className="text-xs text-[#8A8175] text-center break-all">
								ID du produit : {createdProductId}
							</p>
						)}
					</div>
				</div>
			</form>
		</div>
	);
};

export default AdminAddProductPage;
